import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  FormControlLabel, Checkbox, Box
} from "@mui/material";
import { Delete, Add, Edit } from "@mui/icons-material";
import httpClient from "../../_util/api";

const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
function isStrongPassword(password) {
  return STRONG_PASSWORD_REGEX.test(password);
}
function generateStrongPassword() {
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const upper = "ABCDEFGHJKMNPQRSTUVWXYZ";
  const digit = "23456789";
  const special = "@$!%*?&";
  const all = lower + upper + digit + special;
  let p = "";
  p += lower[Math.floor(Math.random() * lower.length)];
  p += upper[Math.floor(Math.random() * upper.length)];
  p += digit[Math.floor(Math.random() * digit.length)];
  p += special[Math.floor(Math.random() * special.length)];
  for (let i = 0; i < 6; i++) p += all[Math.floor(Math.random() * all.length)];
  return p.split("").sort(() => Math.random() - 0.5).join("");
}

// Partner staff users log in to the partner dashboard and see / update the
// same applications that are assigned to this partner.
const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [changePassword, setChangePassword] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const getStaff = async () => {
    try {
      const res = await httpClient.get("/user/partner-staff");
      if (res.status === 200) {
        setStaff(res.data);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to load staff");
    }
  };

  useEffect(() => {
    getStaff();
  }, []);

  const handleOpen = (user = null) => {
    if (user) {
      setEditingId(user._id);
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
      setPassword("");
    } else {
      setPassword(generateStrongPassword());
    }
    setChangePassword(false);
    setError("");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setChangePassword(false);
    setEditingId(null);
    setError("");
  };

  const handleAdd = async () => {
    if (!firstName || !email || !password) {
      setError("First name, email and password are required");
      return;
    }
    if (!isStrongPassword(password)) {
      setError("Password must be 8+ characters with uppercase, lowercase, number and special character (@$!%*?&)");
      return;
    }
    try {
      const res = await httpClient.post("/user/partner-staff", { firstName, lastName, email, password });
      if (res.status === 201) {
        setStaff([res.data, ...staff]);
        handleClose();
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to add staff user");
    }
  };

  const handleUpdate = async () => {
    if (!firstName || !email) {
      setError("First name and email are required");
      return;
    }
    if (changePassword && !isStrongPassword(password)) {
      setError("Password must be 8+ characters with uppercase, lowercase, number and special character (@$!%*?&)");
      return;
    }
    try {
      const payload = { firstName, lastName, email };
      if (changePassword) payload.password = password;
      const res = await httpClient.put(`/user/partner-staff/${editingId}`, payload);
      if (res.status === 200) {
        setStaff(staff.map((u) => (u._id === editingId ? res.data : u)));
        handleClose();
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update staff user");
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm("Delete this staff user? They will no longer be able to log in.")) return;
    try {
      const res = await httpClient.delete(`/user/partner-staff/${staffId}`);
      if (res.status === 200) {
        setStaff(staff.filter((u) => u._id !== staffId));
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete staff user");
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight="bold">
        Manage Staff
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Staff users can view and update all applications assigned to your organization.
      </Typography>
      <Paper sx={{ width: "100%", overflow: "hidden", padding: 2, marginTop: "16px" }}>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#4caf50", color: "white", marginBottom: 2 }}
          startIcon={<Add />}
          onClick={() => handleOpen()}
        >
          Add Staff
        </Button>
        {error && !open && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#4caf50" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>#</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>First Name</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Last Name</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Email</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.length > 0 ? (
                staff.map((user, index) => (
                  <TableRow key={user._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                    <TableCell>{user.lastName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleOpen(user)}><Edit /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(user._id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No staff users yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={open} onClose={handleClose}>
          {error && (
            <Typography color="error" sx={{ pt: 3, pl: 3 }}>
              {error}
            </Typography>
          )}
          <DialogTitle>{editingId ? "Edit Staff User" : "Add Staff User"}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="First Name"
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <TextField
              margin="dense"
              label="Last Name"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {!editingId ? (
              <TextField
                margin="dense"
                label="Password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                error={password.length > 0 && !isStrongPassword(password)}
                helperText={password.length > 0 && !isStrongPassword(password) ? "Use 8+ chars: upper, lower, number, special (@$!%*?&)" : "Auto-filled with a strong password. Share it with the staff user."}
              />
            ) : (
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={changePassword}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setChangePassword(checked);
                        setPassword(checked ? generateStrongPassword() : "");
                      }}
                      sx={{ "&.Mui-checked": { color: "#4caf50" } }}
                    />
                  }
                  label="Change password (optional)"
                />
                {changePassword && (
                  <TextField
                    margin="dense"
                    label="New password"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={password.length > 0 && !isStrongPassword(password)}
                    helperText={password.length > 0 && !isStrongPassword(password) ? "Use 8+ chars: upper, lower, number, special (@$!%*?&)" : ""}
                  />
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#4caf50", color: "white" }}
              onClick={editingId ? handleUpdate : handleAdd}
            >
              {editingId ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </>
  );
};

export default Staff;
